import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { IPagination } from 'src/app/shared/models/pagination';
import { environment } from 'src/environments/environment';
import { IipObservation } from '../interfaces/Iip-observation';
import AllSettlementResponse from '../interfaces/ip-all-settlement-response';
import FilterValues, { SettlementCloseMultipleDto } from '../interfaces/ip-filter-values';
import { PageResponse } from '../interfaces/ip-page-response';
import SalesForSettlement from '../interfaces/ip-sales-for-settlement';
import SettlementAndCurrencyConversionsResponse from '../interfaces/ip-settlement-and-currency-conversion-response';
import SettlementCreateRequest from '../interfaces/ip-settlement-create-request';
import SettlementCurrencyConversionCreateManyRequest from '../interfaces/ip-settlement-currency-conversion-create-many-request';
import SettlementUpdateRequest from '../interfaces/ip-settlement-edit-request';
import SettlementFromSalesRequest from '../interfaces/ip-settlement-from-sales-request';
import SettlementResponse from '../interfaces/ip-settlement-response';
import SettlementSaleAndSettlementResponse from '../interfaces/ip-settlement-sale-and-settlement-response';
import SettlementSaleCreateRequest from '../interfaces/ip-settlement-sale-create-request';
import { SalePaymentRequest } from '../interfaces/ip-settlement-sale-payment-request';
import { SalePaymentResponse } from '../interfaces/ip-settlement-sale-payment-response';
import { UpdateSalePaymentRequest } from '../interfaces/ip-settlement-sale-payment-update';
import SettlementSaleUpdateRequest from '../interfaces/ip-settlement-sale-update-request';
import SettlementStatus from '../interfaces/ip-settlement-status';
import { QueryParametersService } from './ip-query-parameter.service';

@Injectable({
  providedIn: 'root',
})
export class IpSettlementService {
  private api = environment.api;

  private settlementStatusesSubject = new BehaviorSubject<SettlementStatus[] | null>(null); 
  settlementStatuses$ = this.settlementStatusesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private queryParameterService: QueryParametersService
  ) { 
    this.getAllSettlementStatuses().subscribe();
  }

  // SETTLEMENTS

  getData(
    pagination: IPagination,
    sortFieldName: string = '',
    sortOrder: string = '',
    searchName: string = '',
    searchValue: string = '',
    statusCode: string = '',
    billed?: boolean,
    filterValues?: FilterValues
  ): Observable<PageResponse<AllSettlementResponse>> {
    const params = this.queryParameterService.buildQueryParameters(
      sortFieldName,
      sortOrder,
      pagination,
      searchName,
      searchValue,
      filterValues,
      0, // A trick
      0, // A trick
      statusCode,
      billed
    );
    return this.http.get<PageResponse<AllSettlementResponse>>(
      `${this.api}/invopay/settlements`,
      { params }
    );
  }

  getDataBySupplierId(
    supplierId: number,
    pagination: IPagination,
    sortFieldName: string = '',
    sortOrder: string = '',
    searchName: string = '',
    searchValue: string = '',
    statusCode: string = '',
    filterValues?: FilterValues
  ): Observable<PageResponse<AllSettlementResponse>> {
    const params = this.queryParameterService.buildQueryParameters(
      sortFieldName,
      sortOrder,
      pagination,
      searchName,
      searchValue,
      filterValues,
      0, // A trick
      0, // A trick
      statusCode,
    );
    return this.http.get<PageResponse<AllSettlementResponse>>(
      `${this.api}/invopay/settlements/supplier/${supplierId}`,
      { params }
    );
  }

  createSettlement(
    request: SettlementCreateRequest
  ): Observable<SettlementResponse> {
    const formattedBody = this.toFormData(request);
    return this.http.post<SettlementResponse>(
      `${this.api}/invopay/settlements`,
      formattedBody
    );
  }

  createSettlementFromSales(
    request: SettlementFromSalesRequest
  ): Observable<SettlementResponse> {
    const formattedBody = this.toFormData(request);
    return this.http.post<SettlementResponse>(
      `${this.api}/invopay/sales/from-sales`,
      formattedBody
    );
  }

  addSalesToSettlement(
    request: SalesForSettlement,
    settlementId: number
  ): Observable<SettlementResponse> {
    const formattedBody = this.toFormData(request);
    return this.http.put<SettlementResponse>(
      `${this.api}/invopay/sales/settlement/${settlementId}`,
      formattedBody
    );
  }

  getSettlementById(id: number): Observable<SettlementResponse> {
    return this.http.get<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}`
    );
  }

  getApprovedSettlements(
    brokerId?: number,
    currencyCodes?: string[]
  ): Observable<SettlementResponse[]> {
    if (brokerId) {
      let params = new HttpParams()
      if (currencyCodes && currencyCodes.length > 0) {
        params = params.set('currencyCodes', currencyCodes.join(','));
      }
      return this.http.get<SettlementResponse[]>(
        `${this.api}/invopay/settlements/readAllApproved/${brokerId}`,
        { params }
      );
    }
    return this.http.get<SettlementResponse[]>(
      `${this.api}/invopay/settlements/readAllApproved`
    );
  }

  changeSettlementStatus(
    id: number,
    statusCode: string,
    observation?: string
  ): Observable<SettlementResponse> {
    return this.http.post<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}?newStatusCode=${statusCode}&observation=${observation}`,
      {}
    );
  }

  updateSettlement(val: string): Observable<SettlementResponse> {
    return this.http.put<SettlementResponse>(
      `${this.api}/invopay/settlements/edit`,
      val
    );
  }

  editSettlement(
    request: SettlementUpdateRequest
  ): Observable<SettlementResponse> {
    const formattedBody = this.toFormData(request);

    return this.http.put<SettlementResponse>(
      `${this.api}/invopay/settlements/edit`,
      request
    );
  }

  deleteSettlement(id: number): Observable<SettlementResponse> {
    return this.http.delete<SettlementResponse>(
      `${this.api}/invopay/settlements/${id}`
    );
  }

  // SETTLEMENT SALES

  createSaleToSettlementSale(
    settlementSaleRequest: SettlementSaleCreateRequest
  ): Observable<SettlementSaleAndSettlementResponse> {
    return this.http.post<SettlementSaleAndSettlementResponse>(
      `${this.api}/invopay/settlements/sales`,
      settlementSaleRequest
    );
  }

  updateSettlementSale(
    request: SettlementSaleUpdateRequest,
    id: number
  ): Observable<SettlementSaleAndSettlementResponse> {
    return this.http.put<SettlementSaleAndSettlementResponse>(
      `${this.api}/invopay/settlements/sales/${id}`,
      request
    );
  }

  deleteSettlementSale(
    id: number
  ): Observable<SettlementSaleAndSettlementResponse> {
    return this.http.delete<SettlementSaleAndSettlementResponse>(
      `${this.api}/invopay/settlements/sales/${id}`
    );
  }

  // SALE PAYMENTS;

  createSalePayment(
    request: SalePaymentRequest
  ): Observable<SalePaymentResponse> {
    return this.http.post<SalePaymentResponse>(
      `${this.api}/invopay/settlements/sales/payments/add`,
      request
    );
  }

  editSalePayment(
    request: UpdateSalePaymentRequest,
    settlementId?: number
  ): Observable<SalePaymentResponse> {
    let url = `${this.api}/invopay/settlements/sales/payments/${request.id}`;
    const params = settlementId != undefined ? `?settlementId=${settlementId}` : '';

    return this.http.put<SalePaymentResponse>(url + params, request);
  }

  deleteSalePayment(idPayment: number): Observable<SalePaymentResponse> {
    return this.http.delete<SalePaymentResponse>(
      `${this.api}/invopay/settlements/sales/payments/${idPayment}`
    );
  }

  // SETTLEMENT CURRENCY CONVERSIONS

  sendCurrencyConversions(
    request: SettlementCurrencyConversionCreateManyRequest
  ): Observable<SettlementAndCurrencyConversionsResponse> {
    return this.http.post<SettlementAndCurrencyConversionsResponse>(
      `${this.api}/invopay/settlements/currency-conversions`,
      request
    );
  }

  // SETTLEMENT STATUSES AND STATUS TRANSITIONS

  getAllSettlementStatuses(): Observable<Array<SettlementStatus>> {
    return this.http.get<Array<SettlementStatus>>(
      `${this.api}/invopay/settlements/status`
    ).pipe(
      tap((statuses) => this.settlementStatusesSubject.next(statuses))
    )
  }

  getNextPossibleStatuses(
    statusCode: string
  ): Observable<Array<SettlementStatus>> {
    return this.http.get<Array<SettlementStatus>>(
      `${this.api}/invopay/settlements/status-transitions/${statusCode}`
    );
  }

  // OTHERS

  toFormData(formValue: any) {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      let value = formValue[key];

      if (value !== null) formData.append(key, value);
    }
    return formData;
  }

  // COMMENTS

  getSettlementObservations(id: number): Observable<IipObservation[]> {
    return this.http.get<IipObservation[]>(
      `${this.api}/invopay/observations/settlement/${id}`
    );
  }

  createSettlementObservation(
    id: number,
    description: string
  ): Observable<IipObservation> {
    return this.http.post<IipObservation>(
      `${this.api}/invopay/observations/settlement?settlementId=${id}`,
      { description }
    );
  }

  sendSettlementInformationEmailPdf(
    settlementId: number,
  ): Observable<void> {
    return this.http.post<void>(`${this.api}/invopay/settlements/send-email/${settlementId}`,
      {});
  }

  changeAllSettlementToClose(
    ids: number[],
    allSelected: boolean,
    filters?: FilterValues
  ): Observable<SettlementResponse[]> {
    const requestDto: SettlementCloseMultipleDto = {
      ids,
      allSelected,
      filters
    }
    return this.http.post<SettlementResponse[]>(
      `${this.api}/invopay/settlements/closeAll`,
      requestDto
    );
  }

  countOpenSettlements(
    filters?: FilterValues
  ): Observable<{ count: number }> {
    return this.http.post<{ count: number }>(
      `${this.api}/invopay/settlements/count_open`,
      filters
    );
  }

  getSettlementPdf(settlementId: number): Observable<Blob> {
    return this.http.get(`${this.api}/invopay/settlements/send-email/${settlementId}`, {
      responseType: 'blob'
    });
  }

  downloadReport(filterValues?: FilterValues, billed?: boolean): Observable<Blob> {
    let params = this.queryParameterService.buildQueryParametersByFilterValues(filterValues);
    if (billed !== undefined) {
      params = params.set('billed', String(billed));
    }
    return this.http.get(`${this.api}/invopay/settlements/download-report-xlsx`, {
      params,
      responseType: 'blob'
    });
  }

}
